/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { type RefObject, useEffect } from 'react';
import { type FlatList, Platform } from 'react-native';

interface UseDropdownOverflowScrollOptions {
  flatListRef?: RefObject<FlatList<any> | null>;
  containerIds?: string[];
  portalHostIds?: string[];
  margin?: number;
  settleDelay?: number;
}

export const useDropdownOverflowScroll = ({
  flatListRef,
  containerIds = ['flights-search-container'],
  portalHostIds = ['web-portal-host'],
  margin = 16,
  settleDelay = 100,
}: UseDropdownOverflowScrollOptions = {}) => {
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      Platform.OS !== 'web'
    ) {
      return;
    }

    let activeDropdownElement: HTMLElement | null = null;
    let hasScrolledForCurrentDropdown = false;
    let settleTimeoutId: ReturnType<typeof setTimeout> | null = null;

    /**
     * Calculates the true visible bottom on screen for an element,
     * clamping against any parent scroll/clipping containers (overflow: hidden/auto/scroll)
     * so that scrollable list content does not artificially expand the measured height.
     */
    const getVisibleBottom = (
      el: HTMLElement,
      container: HTMLElement,
      initialBottom: number,
    ): number => {
      let visibleBottom = initialBottom;
      let curr: HTMLElement | null = el.parentElement;

      while (
        curr &&
        curr !== container &&
        curr !== document.body &&
        curr !== document.documentElement
      ) {
        const style = window.getComputedStyle(curr);
        const overflowY = style.overflowY || style.overflow;
        if (
          overflowY === 'hidden' ||
          overflowY === 'auto' ||
          overflowY === 'scroll'
        ) {
          const parentBottom = curr.getBoundingClientRect().bottom;
          if (parentBottom < visibleBottom) {
            visibleBottom = parentBottom;
          }
        }
        curr = curr.parentElement;
      }
      return visibleBottom;
    };

    const isCalendarElement = (el: HTMLElement): boolean => {
      return (
        el.id === 'calendar-dropdown-container' ||
        Boolean(el.closest?.('#calendar-dropdown-container'))
      );
    };

    const performCheck = () => {
      const windowHeight = window.innerHeight;
      let maxBottom = 0;
      let targetDropdown: HTMLElement | null = null;

      // 1. Direct children / descendants of portal hosts (if any portals are used)
      portalHostIds.forEach((id) => {
        const host = document.getElementById(id);
        if (host) {
          const elements = host.getElementsByTagName('*');
          for (let i = 0; i < elements.length; i++) {
            const child = elements[i];
            if (!(child instanceof HTMLElement)) continue;
            if (!isCalendarElement(child)) continue;

            const r = child.getBoundingClientRect();
            // Exclude fullscreen background overlays (top: 0, bottom: windowHeight)
            if (
              r.height > 0 &&
              r.width > 0 &&
              !(r.top === 0 && Math.abs(r.bottom - windowHeight) < 4)
            ) {
              if (r.bottom > windowHeight && r.bottom > maxBottom) {
                maxBottom = r.bottom;
                targetDropdown = child;
              }
            }
          }
        }
      });

      // 2. Search inside containers, checking only calendar dropdown elements
      containerIds.forEach((id) => {
        const container = document.getElementById(id);
        if (!container) return;

        const elements = container.getElementsByTagName('*');
        for (let i = 0; i < elements.length; i++) {
          const el = elements[i];
          if (!(el instanceof HTMLElement)) continue;
          if (el.offsetWidth <= 0 || el.offsetHeight <= 0) continue;

          const style = window.getComputedStyle(el);
          if (
            (style.position !== 'absolute' && style.position !== 'fixed') ||
            style.display === 'none' ||
            style.visibility === 'hidden' ||
            style.opacity === '0' ||
            Number(style.zIndex) < 0
          ) {
            continue;
          }

          if (!isCalendarElement(el)) continue;

          const r = el.getBoundingClientRect();
          const visibleBottom = getVisibleBottom(el, container, r.bottom);
          if (
            visibleBottom > windowHeight &&
            visibleBottom > maxBottom &&
            !(r.top === 0 && Math.abs(visibleBottom - windowHeight) < 4)
          ) {
            maxBottom = visibleBottom;
            targetDropdown = el;
          }
        }
      });

      // When no dropdown is overflowing the window, reset the logged state
      if (!targetDropdown || maxBottom <= windowHeight) {
        activeDropdownElement = null;
        hasScrolledForCurrentDropdown = false;
        return;
      }

      const currentDropdown: HTMLElement = targetDropdown;

      // If we already auto-scrolled for this active dropdown, do not interrupt the user again
      if (
        hasScrolledForCurrentDropdown &&
        activeDropdownElement !== null &&
        (activeDropdownElement === currentDropdown ||
          activeDropdownElement.contains(currentDropdown) ||
          currentDropdown.contains(activeDropdownElement))
      ) {
        return;
      }

      const overflow = maxBottom - windowHeight;
      activeDropdownElement = currentDropdown;
      hasScrolledForCurrentDropdown = true;
      const scrollPixels = Math.ceil(overflow) + margin;

      const findAndScroll = () => {
        const flatListNode =
          flatListRef?.current?.getScrollableNode?.() as HTMLElement | null;

        // 1. Direct FlatList node
        if (
          flatListNode &&
          flatListNode.scrollHeight > flatListNode.clientHeight + 10
        ) {
          flatListNode.scrollBy({ top: scrollPixels, behavior: 'smooth' });
          return;
        }

        // 2. Ascend DOM tree to find any scrollable parent (e.g. WebLayoutView container)
        let curr: HTMLElement | null = flatListNode;
        while (
          curr &&
          curr !== document.body &&
          curr !== document.documentElement
        ) {
          const s = window.getComputedStyle(curr);
          const ov = s.overflowY || s.overflow;
          if (
            (ov === 'auto' || ov === 'scroll') &&
            curr.scrollHeight > curr.clientHeight + 10
          ) {
            curr.scrollBy({ top: scrollPixels, behavior: 'smooth' });
            return;
          }
          curr = curr.parentElement;
        }

        // 3. Fallback: scrollable container in document or window
        if (document.scrollingElement) {
          document.scrollingElement.scrollBy({
            top: scrollPixels,
            behavior: 'smooth',
          });
        }
        window.scrollBy({ top: scrollPixels, behavior: 'smooth' });
      };

      findAndScroll();
    };

    const checkOverflow = () => {
      if (settleTimeoutId) return;

      settleTimeoutId = setTimeout(() => {
        settleTimeoutId = null;
        performCheck();
      }, settleDelay);
    };

    const observer =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(checkOverflow)
        : null;

    if (observer) {
      const allHostIds = Array.from(
        new Set([...containerIds, ...portalHostIds]),
      );
      allHostIds.forEach((id) => {
        const host = document.getElementById(id);
        if (host) {
          observer.observe(host, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'],
          });
        }
      });
    }

    window.addEventListener('resize', checkOverflow);

    checkOverflow();

    return () => {
      if (settleTimeoutId) {
        clearTimeout(settleTimeoutId);
      }
      observer?.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [flatListRef, containerIds, portalHostIds, margin, settleDelay]);
};
